<!-- 招标平台的添加 / 编辑表单弹窗 -->
<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="60%">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="200px"
    >
      <el-row>
        <el-col :span="12">
          <el-form-item label="收费标准" prop="feeStandard">
            <el-select v-model="formData.feeStandard" class="w-1/1" placeholder="请选择收费标准">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_BID_PLATFORM_FEE_STANDARD)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="类型" prop="type">
            <el-select v-model="formData.type" class="w-1/1" placeholder="请选择类型">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_BID_PLATFORM_TYPE)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="平台名称" prop="name">
            <el-input v-model="formData.name" placeholder="请输入平台名称" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="网址" prop="website">
            <el-input v-model="formData.website" placeholder="请输入网址，如 https://xxx.com/" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="第三方评价" prop="thirdPartyEvaluation">
            <el-input
              v-model="formData.thirdPartyEvaluation"
              :rows="3"
              placeholder="请输入第三方评价"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="站长工具统计具体的平台流量分析" prop="trafficAnalysis">
            <el-input
              v-model="formData.trafficAnalysis"
              :rows="3"
              placeholder="请输入流量分析"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="账号/密码" prop="accountPassword">
            <el-input
              v-model="formData.accountPassword"
              :rows="2"
              placeholder="请输入账号/密码"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="合作状态" prop="cooperationStatus">
            <el-select
              v-model="formData.cooperationStatus"
              class="w-1/1"
              clearable
              placeholder="请选择合作状态"
            >
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_BID_PLATFORM_COOPERATION_STATUS)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="费用说明" prop="feeDesc">
            <el-input
              v-model="formData.feeDesc"
              :rows="3"
              placeholder="请输入费用说明"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="备注" prop="remark">
            <el-input
              v-model="formData.remark"
              :rows="2"
              placeholder="请输入备注"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">保 存</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { BidPlatformApi } from '@/api/crm/bidPlatform'

defineOptions({ name: 'BidPlatformForm' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中
const formType = ref('') // 表单的类型：create - 新增；update - 修改

/** 空表单数据 */
const emptyFormData = () => ({
  id: undefined,
  feeStandard: undefined,
  type: undefined,
  name: undefined,
  website: undefined,
  thirdPartyEvaluation: undefined,
  trafficAnalysis: undefined,
  accountPassword: undefined,
  cooperationStatus: undefined,
  feeDesc: undefined,
  remark: undefined
})

const formData = ref<any>(emptyFormData())
const formRules = reactive({
  feeStandard: [{ required: true, message: '收费标准不能为空', trigger: 'change' }],
  type: [{ required: true, message: '类型不能为空', trigger: 'change' }],
  name: [{ required: true, message: '平台名称不能为空', trigger: 'blur' }]
})
const formRef = ref() // 表单 Ref

/** 打开弹窗 */
const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = type === 'create' ? '新增招标平台汇总' : '编辑招标平台汇总'
  formType.value = type
  resetForm()
  // 修改时，加载数据
  if (id) {
    formLoading.value = true
    try {
      const data = await BidPlatformApi.getBidPlatform(id)
      formData.value = {
        id: data.id,
        feeStandard: data.feeStandard,
        type: data.type,
        name: data.name,
        website: data.website,
        thirdPartyEvaluation: data.thirdPartyEvaluation,
        trafficAnalysis: data.trafficAnalysis,
        accountPassword: data.accountPassword,
        cooperationStatus: data.cooperationStatus,
        feeDesc: data.feeDesc,
        remark: data.remark
      }
    } finally {
      formLoading.value = false
    }
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  await formRef.value.validate()
  // 提交请求
  formLoading.value = true
  try {
    const data = { ...formData.value }
    if (formType.value === 'create') {
      await BidPlatformApi.createBidPlatform(data)
      message.success(t('common.createSuccess'))
    } else {
      await BidPlatformApi.updateBidPlatform(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = emptyFormData()
  formRef.value?.resetFields()
}
</script>
